import styles from '@/styles/Home.module.scss'
import { StampComponents } from '@/components/molecules'
import type React from 'react'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface Spot {
  address: string
  id: number
  name: string
  zip: string
}

interface IStampSheetImageSize {
  width: number
  height: number
  index: number
}

interface StampSheetProps {
  idx: number
  sheet: Api.UserSheetObject
  campaign: {
    isRepeatStamp: boolean
    stampShape: number
    sheetMode: Api.CampaignSheetMode
  }
  // spots は正直どこで利用しているか不明だが、既存の処理を壊さないために付与
  onStampClick: (spots: Spot[]) => void
  // sheetMode === "OVERLAY" の場合は、画像のサイズを取得するために必要
  setStampSheetImageSize: React.Dispatch<React.SetStateAction<IStampSheetImageSize[]>>
}

/**
 * campaign.type === 1の、通常のスタンプの場合のシート
 * */
export const StampSheet = ({ idx, sheet, campaign, onStampClick, setStampSheetImageSize }: StampSheetProps) => {
  const { t } = useTranslation()
  const [imageSize, setImageSize] = useState<{
    natural: { width: number; height: number } | null
    rendered: { width: number; height: number } | null
    fixed: { width: number; height: number } | null
  }>({ natural: null, rendered: null, fixed: { width: 100, height: 100 } })

  const [screenSize, setScreenSize] = useState<{
    width: number
    height: number
  }>({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  })

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    // 初期サイズを設定
    handleResize()

    // リサイズイベントリスナーを追加
    window.addEventListener('resize', handleResize)

    // クリーンアップ関数
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget
    const naturalSize = {
      width: img.naturalWidth,
      height: img.naturalHeight,
    }
    const renderedSize = {
      width: img.clientWidth,
      height: img.clientHeight,
    }
    const fixedWidth = (screenSize.width * 11) / 12 // 画面幅から余白を引いた高さ
    const fixedHeight = Math.floor((fixedWidth * (img.naturalHeight || 1)) / (img.naturalWidth || 1))

    setImageSize({
      natural: naturalSize,
      rendered: renderedSize,
      fixed: { width: fixedWidth, height: fixedHeight },
    })
    // 40pxはmtとして追加されている分
    setStampSheetImageSize((prev) => {
      const newEntry = { width: fixedWidth, height: fixedHeight + 40, index: idx }
      const found = prev.some((item) => item.index === idx)
      if (found) {
        return prev.map((item) => (item.index === idx ? newEntry : item))
      } else {
        return [...prev, newEntry]
      }
    })
  }
  if (campaign.sheetMode === 'NORMAL' || campaign.sheetMode === 'BACKGROUND') {
    // それぞれの場合に応じたstyleを設定
    const style =
      campaign.sheetMode === 'NORMAL'
        ? { backgroundColor: 'var(--main-color-20)' }
        : campaign.sheetMode === 'BACKGROUND'
          ? {
              backgroundImage: `url(${sheet.backgroundImage})`,
              backgroundRepeat: 'repeat-y',
              backgroundSize: '100% auto',
            }
          : {}
    return (
      <div key={`div-${sheet.id}`} className={`mt-[40px] rounded-lg pt-6`} style={style}>
        <div className="relative">
          <h2 className="px-3 text-center text-sm font-bold">{sheet.name}</h2>
        </div>
        <div className="mt-5 mr-3 flex items-center justify-end">
          <div className={styles.c_stamp_len}>
            <p>{t('home.number_of_collected_stamps', 'スタンプ獲得数')}</p>
            <p className="ml-4">
              {campaign.isRepeatStamp ? (
                <>{sheet.hasCount}</>
              ) : (
                <>
                  {sheet.hasCount} / {sheet.total}
                </>
              )}
            </p>
          </div>
        </div>
        <div className={`flex flex-wrap ${styles.c_stamp_contents_wrap}`}>
          {sheet.stamps?.map((contentStamp, i: number) => {
            const stampNotAcquired = contentStamp.image.endsWith('system/non-stamp.png')
            const stampShape = stampNotAcquired ? 'circular' : campaign.stampShape === 0 ? 'circular' : 'square'
            return (
              <StampComponents
                key={`slide-${sheet.id}-stamp-cmp-${contentStamp.spotId}-${i}`}
                query={contentStamp.spotId}
                hasCount={contentStamp.hasCount}
                course={sheet.name}
                img={contentStamp.image}
                name={contentStamp.name}
                spots={contentStamp.spots}
                isRepeatStamp={campaign?.isRepeatStamp}
                stampShape={stampShape}
                onGroupModal={() => onStampClick(contentStamp.spots)}
              />
            )
          })}
        </div>
      </div>
    )
  }
  if (campaign.sheetMode === 'OVERLAY') {
    return (
      <div
        className={`mt-[40px] rounded-lg pt-6 relative`}
        style={{
          minHeight: imageSize.fixed.height,
          height: imageSize.fixed.height,
          minWidth: imageSize.fixed.width,
          width: imageSize.fixed.width,
        }}
      >
        <img
          key={`img-1-${sheet.id}`}
          src={sheet.backgroundImage}
          alt={''}
          className={`absolute top-0 left-0 w-full h-full object-contain`}
          onLoad={handleImageLoad}
        />
        {sheet.stamps && (
          <>
            {sheet.stamps.map((v, idx) => {
              if (v.hasCount >= 1) {
                return (
                  <img
                    key={v.name}
                    src={v.overlayImage}
                    alt={v.name}
                    className="absolute top-0 left-0 w-full h-full object-contain"
                  />
                )
              }
            })}
          </>
        )}
      </div>
    )
  }
}
